/* Document-space, two-dimensional paint. A staggered velocity grid, free-surface
   pressure solve and conservative pigment transport keep paint inside the page.
   Bounded resolution and fixed timesteps keep the opt-in effect mobile-friendly. */
(function(root) {
  'use strict';
  class NyeTiltFluid {
    constructor(width, height) {
      this.drained = 0; this.time = 0;
      this.build(width, height);
    }
    build(width, height) {
      this.width = width; this.height = height;
      this.cols = Math.max(14, Math.min(48, Math.ceil(width / 22)));
      this.rows = Math.max(2, Math.min(1200, Math.ceil(height / (width / this.cols))));
      this.dx = width / this.cols; this.dy = height / this.rows;
      this.area = this.dx * this.dy;
      const n = this.cols * this.rows;
      this.m = new Float64Array(n);
      this.dye = Array.from({length:3}, () => new Float64Array(n));
      this.u = new Float64Array((this.cols + 1) * this.rows);
      this.v = new Float64Array(this.cols * (this.rows + 1));
      this.p = new Float64Array(n); this.nextP = new Float64Array(n);
      this.div = new Float64Array(n); this.out = new Float64Array(n);
      this.dm = new Float64Array(n);
      this.dd = Array.from({length:3}, () => new Float64Array(n));
      this.active = [];
    }
    get volume() { return this.m.reduce((a,b) => a+b, 0) * this.area; }
    get fraction() { return this.volume / (this.width * this.height); }
    pigmentTotals() { return this.dye.map(a => a.reduce((s,v) => s+v,0)*this.area); }
    colorAt(k) { return this.m[k] > 1e-10 ? this.dye.map(a => a[k]/this.m[k]) : [198,128,173]; }
    index(x,y) {
      return Math.max(0,Math.min(this.rows-1,Math.floor(y/this.dy)))*this.cols + Math.max(0,Math.min(this.cols-1,Math.floor(x/this.dx)));
    }
    contains(x,y) { return this.m[this.index(x,y)] > .32; }
    add(x,y,area,color) {
      const accepted = Math.max(0,Math.min(area,this.width*this.height-this.volume));
      const k = this.index(x,y), mass = accepted/this.area;
      this.m[k] += mass;
      this.dye.forEach((a,c) => { a[k] += mass*color[c]; });
      return accepted;
    }
    importSurface(surface) {
      for(let x=0;x<this.cols;x++) {
        const sx=(x+.5)*this.dx;
        const k=Math.min(surface.n-1,Math.floor(sx/surface.dx));
        const top=this.height-surface.h[k], color=surface.colorAt(k);
        for(let y=Math.max(0,Math.floor(top/this.dy));y<this.rows;y++) {
          const i=y*this.cols+x, mass=Math.max(0,Math.min(1,((y+1)*this.dy-top)/this.dy));
          this.m[i]=mass; this.dye.forEach((a,c)=>{a[i]=mass*color[c];});
        }
      }
      // Sampling different column widths must not create or destroy paint/dye.
      const actual=this.volume, wanted=surface.volume;
      if(actual>0) {
        const ratio=wanted/actual;
        this.m.forEach((v,i)=>{this.m[i]=v*ratio;});
        const totals=this.pigmentTotals(), targets=surface.pigmentTotals();
        this.dye.forEach((a,c)=>a.forEach((v,i)=>{a[i]=totals[c]>0?v*targets[c]/totals[c]:0;}));
      } else if(wanted>0) this.add(this.width/2,this.height-1,wanted,surface.colorAt(surface.n/2));
      this.drained=surface.drained;
    }
    resize(width,height) {
      if(Math.abs(width-this.width)<.5 && Math.abs(height-this.height)<.5) return;
      const old={cols:this.cols,rows:this.rows,dx:this.dx,dy:this.dy,width:this.width,height:this.height,area:this.area,m:this.m,dye:this.dye};
      const before=this.volume;
      this.build(width,height);
      // Redistribute normalized document positions, retaining physical volume.
      old.m.forEach((mass,k)=>{
        if(mass<=0)return;
        const x=((k%old.cols)+.5)*old.dx/old.width*width;
        const y=(Math.floor(k/old.cols)+.5)*old.dy/old.height*height;
        const i=this.index(x,y), scale=old.area/this.area;
        this.m[i]+=mass*scale;this.dye.forEach((a,c)=>{a[i]+=old.dye[c][k]*scale;});
      });
      if(before>width*height) {
        const ratio=width*height/before;
        this.m.forEach((v,k)=>{this.m[k]=v*ratio;});
        this.dye.forEach(a=>a.forEach((v,k)=>{a[k]=v*ratio;}));
        this.drained+=before-width*height;
      }
    }
    clear() {
      this.m.fill(0);this.dye.forEach(a=>a.fill(0));this.u.fill(0);this.v.fill(0);this.p.fill(0);this.drained=0;
    }
    step(dt, gravity, draining=false) {
      this.time+=Math.min(.05,Math.max(0,dt));
      const fixed=1/90;
      while(this.time>=fixed) {this.tick(fixed,gravity,draining);this.time-=fixed;}
    }
    tick(dt,g,draining) {
      const w=this.cols,h=this.rows,m=this.m,dx=this.dx,dy=this.dy;
      const damp=Math.exp(-1.15*dt), limit=Math.min(dx,dy)*.65/dt;
      const clamp=v=>Math.max(-limit,Math.min(limit,v));
      // Faces outside the document are solid. Dry faces carry no old momentum.
      for(let y=0;y<h;y++)for(let x=1;x<w;x++) {
        const a=y*w+x-1,b=a+1,f=y*(w+1)+x;
        this.u[f]=m[a]+m[b]>.00001?clamp((this.u[f]+g.x*1180*dt)*damp):0;
      }
      for(let y=1;y<h;y++)for(let x=0;x<w;x++) {
        const a=(y-1)*w+x,b=a+w,f=y*w+x;
        this.v[f]=m[a]+m[b]>.00001?clamp((this.v[f]+g.y*1180*dt)*damp):0;
      }
      this.active.length=0;this.p.fill(0);this.nextP.fill(0);
      for(let y=0;y<h;y++)for(let x=0;x<w;x++) {
        const i=y*w+x;if(m[i]<.01)continue;
        this.active.push(i);
        this.div[i]=(this.u[y*(w+1)+x+1]-this.u[y*(w+1)+x])/dx+(this.v[(y+1)*w+x]-this.v[y*w+x])/dy;
        // Deposited streams may briefly exceed one cell. Pressure releases that
        // excess conservatively rather than deleting or clamping their volume.
        this.div[i]-=Math.max(0,m[i]-1)*.35/dt;
      }
      const ix=1/(dx*dx),iy=1/(dy*dy);
      for(let iter=0;iter<22;iter++) {
        for(const i of this.active) {
          const x=i%w,y=Math.floor(i/w);let sum=0,denom=0;
          if(x>0){sum+=this.p[i-1]*ix;denom+=ix;}
          if(x<w-1){sum+=this.p[i+1]*ix;denom+=ix;}
          if(y>0){sum+=this.p[i-w]*iy;denom+=iy;}
          if(y<h-1){sum+=this.p[i+w]*iy;denom+=iy;}
          this.nextP[i]=(sum-this.div[i]/dt)/denom;
        }
        const swap=this.p;this.p=this.nextP;this.nextP=swap;
      }
      for(let y=0;y<h;y++)for(let x=1;x<w;x++) {
        const a=y*w+x-1,b=a+1,f=y*(w+1)+x;
        if(m[a]+m[b]>.00001)this.u[f]=clamp(this.u[f]-dt*(this.p[b]-this.p[a])/dx);
      }
      for(let y=1;y<h;y++)for(let x=0;x<w;x++) {
        const a=(y-1)*w+x,b=a+w,f=y*w+x;
        if(m[a]+m[b]>.00001)this.v[f]=clamp(this.v[f]-dt*(this.p[b]-this.p[a])/dy);
      }
      // Shared face fluxes transport mass and all three pigments together.
      this.out.fill(0);this.dm.fill(0);this.dd.forEach(a=>a.fill(0));
      const faces=(visit)=>{
        for(let y=0;y<h;y++)for(let x=1;x<w;x++) {
          const a=y*w+x-1,b=a+1,v=this.u[y*(w+1)+x],donor=v>=0?a:b;
          visit(donor,v>=0?b:a,Math.abs(v)*dt/dx*m[donor]);
        }
        for(let y=1;y<h;y++)for(let x=0;x<w;x++) {
          const a=(y-1)*w+x,b=a+w,v=this.v[y*w+x],donor=v>=0?a:b;
          visit(donor,v>=0?b:a,Math.abs(v)*dt/dy*m[donor]);
        }
      };
      faces((a,b,flow)=>{this.out[a]+=flow;});
      faces((a,b,flow)=>{
        if(flow<=0||m[a]<=0)return;
        flow*=Math.min(1,m[a]/Math.max(this.out[a],1e-12));
        this.dm[a]-=flow;this.dm[b]+=flow;
        this.dye.forEach((d,c)=>{const pigment=flow*d[a]/m[a];this.dd[c][a]-=pigment;this.dd[c][b]+=pigment;});
      });
      m.forEach((v,i)=>{m[i]=Math.max(0,v+this.dm[i]);});
      this.dye.forEach((a,c)=>a.forEach((v,i)=>{a[i]=Math.max(0,v+this.dd[c][i]);}));
      if(draining) {
        const volume=this.volume,take=Math.min(volume,this.width*Math.max(18,Math.sqrt(280*volume/this.width)*.8)*dt);
        if(volume>0){const r=1-take/volume;m.forEach((v,i)=>{m[i]=v*r;});this.dye.forEach(a=>a.forEach((v,i)=>{a[i]=v*r;}));this.drained+=take;}
      }
    }
  }
  // accelerationIncludingGravity uses device axes; screen orientation rotates
  // those axes again. Upright portrait rest is approximately (0,-9.81,0).
  function screenGravity(acceleration, angle=0) {
    const a=angle*Math.PI/180,x=Number(acceleration.x),y=Number(acceleration.y);
    if(!Number.isFinite(x)||!Number.isFinite(y))return null;
    let gx=(x*Math.cos(a)-y*Math.sin(a))/9.81;
    let gy=(-x*Math.sin(a)-y*Math.cos(a))/9.81;
    const length=Math.hypot(gx,gy);
    if(length>1.35){gx*=1.35/length;gy*=1.35/length;}
    return {x:gx,y:gy};
  }
  NyeTiltFluid.screenGravity=screenGravity;
  if(typeof module!=='undefined'&&module.exports)module.exports=NyeTiltFluid;
  else root.NyeTiltFluid=NyeTiltFluid;
})(typeof window!=='undefined'?window:globalThis);
